import Animation from 'animation'
import * as d3 from 'd3-selection'
import * as array from 'd3-array'
import { interpolate } from 'd3-interpolate'
import * as shape from 'd3-shape'
import PathAdjuster from '../path/adjuster'
import points from '../points'
import { EPSILON } from 'chart-constants'
import lineSize from '../../../lib/line-size'
import Forecast from '../forecast'
import * as colors from 'colors'

class LineAreaAnimation extends Animation {

  constructor (options) {
    super(options)

    this.curve = shape.curveLinear // default

    this.styles = {
      lineStroke: this.chart.getPropertyValue('--line-path-stroke-width'),
      areaStroke: this.chart.getPropertyValue('--area-path-stroke-width'),
      areaOpacity: this.chart.getPropertyValue('--area-path-opacity')
    }
  }

  configure (axis, serie) {
    super.configure(axis, serie)

    let xScale = this._scalerX(this.scalers.x)

    this.pathAdjuster = new PathAdjuster({
      chart: this.chart,
      curve: this.curve,
      xScale,
      previous: this.previous,
      yScale: this.yScale
    })

    let dimensions = this.chart.datum().dimensions

    this.size = lineSize(dimensions.width, dimensions.height, this.scalers.x.groupScale.domain().length)

    this.forecast = new Forecast({
      serie,
      xScale,
      yScale: this.yScale,
      chart: this.chart,
      previous: this.previous,
      styles: this.styles
    })

    return this
  }

  bindAndAnimate (container, node) {
    let serie = d3.select(node).datum()
      , svg = d3.select(node.ownerSVGElement)
      , group = d3.select(node)
                  .selectAll(`.${serie.type}-group`)
                  .data(d => [d])
      , enter = group.enter()
                     .append('g')
                     .attr('class', `.${serie.type}-group`)
      , transition = group.merge(enter)
                          .attr('class', `${serie.type}-group ${this.size.clazz}`)
                          .attr('clip-path', `url(#${svg.attr('id')}-chart-clip-path-plot-height-${this.plotId})`)
                          .transition(container)
      , exit = this.exitingSeries.filter(exiting => exiting.id === serie.id)

    this.animate(serie, enter, group, transition, exit)

    this._setColors(transition.selection())
    // Store a clone of the points so have access to them in animations
    node.__previousPoints = serie.points.slice()
  }

  _setColors (selection) {
    let serie = selection.datum()
      , stroke = serie.type === 'line' ? colors.lookup(serie.color) :
      colors.mixBlack(colors.lookupObj(serie.color).rgba, .12)

    selection.selectAll('path.line:not(.outer-line)')
             .style('stroke', stroke)
    selection.selectAll('path.area')
             .style('fill', colors.mixWhite(colors.lookupObj(serie.color).rgba, .3))
  }

  /*
   * Inserts the path elements into the `selection`
   *
   * Returns the selection.
   */
  _insertPaths (selection, scalers) {
    this.pathAdjuster.insert(selection, scalers)

    this._setColors(selection)

    return selection
  }

  _initialInsert (entering) {
    // Add the lines
    this._insertPaths(entering)

    let scalers = {
      x: this._scalerX(this.scalers.x),
      y: this.yScale
    }

    // Adds the points
    this._points(entering, scalers, scalers) // Use the final position scaler for both enter & update

    // The points shouldn't be visible at first
    entering.selectAll('.point:not(.head)')
            .style('display', 'none')

    this.forecast.render(entering)
  }

  /*
   * Returns the length of the `path` element within the selection to
   * the `x` location. This provides us the way to find the sublength
   * of a svg path up to the `x`
   */
  _subLength (selection, x) {
    let path = selection.selectAll('path').node()
      , pathLength = path.getTotalLength()
      , length = 0

    while (length < pathLength && x >= path.getPointAtLength(length).x) {
      length++
    }

    return length
  }


  /*
   * Rebinds the points to the line path and draws the line path
   */
  _rebindPaths (selection, points) {
    this.pathAdjuster.rebind(selection, points)
  }

  /*
   * Returns a tween function that satisfies the requirements of d3's
   * `tween(...)`. This will move the the entire line drawing for each
   * point. We adjust the `points` object which contains all points defined
   * on the entire drawing. That's needed since svg `path` elements need to be
   * transitioned as a whole, rather than individual points.
   */
  _tweenPoint (point, points, interpolateX, interpolateY0, interpolateY1) {
    let datum = d3.select(point).datum()
      , rangeId = datum.rangeId
      , raw = datum.raw // Used so the generators can determine if a point is defined

    return t => {
      let x = interpolateX(t)
        , y0 = interpolateY0(t)
        , y1 = interpolateY1(t)

      points[rangeId] = {
        x, y0, y1, raw
      }

      this.pathAdjuster.updatePathWithPoints(d3.select(point.parentNode), Object.values(points))

      d3.select(point) // Move this point
        .attr('transform', `translate(${x}, ${y1})`)
    }
  }

  /*
   * Returns a tween function that can be used for a `.point`.
   * The tween function will use the `fromSerie` as a starting point of all
   * points used to draw the path. It will then transition to the end point
   * returned by `toLookup.`
   */
  _splitTweener (fromSerie, toLookup) {
    let points = this._getPointsByRange(fromSerie.points)
      , self = this

    return function (d) {
      let to = toLookup(d)
        , interpolateX = interpolate(self.previous.scalers.x.center(d.rangeId), self.scalers.x.center(to.rangeId))
        , interpolateY0 = interpolate(0, 0) // Lines don't really have a y0, so they'll go from 0 to 0
        , interpolateY1 = interpolate(points[d.rangeId].y1, self.yScale(to._range[1]))

      return self._tweenPoint(this, points, interpolateX, interpolateY0, interpolateY1)
    }
  }

  /*
   * Redraws the line drawing, path + points based on latest bound data
   */
  _updateDrawing (selection) {
    // Update the points
    this._points(selection)

    this.pathAdjuster.updateDrawing(selection)

    this.forecast.render(selection)
  }

  /*
   * Receives an array of points that were bound to some selection.
   * Returns an array of the points and their locations, key'd by the rangeId. Fills
   * in the gaps where we might be missing `points`
   */
  _getPreviousPointsByRange (points) {
    let prev = this.previous.scalers.x.groupScale.domain().map(rangeId => {
      let point = points.find(point => point.rangeId === rangeId)
      // In case `oldPoints` is missing points b/c that rangeId didn't have a raw value,
      // determine the pointsByRangeId using the previous group scale domain
      return Object.assign({rangeId, _range: []}, point)
    })

    return this._getPointsByRange(prev) // Get points using data in the DOM
  }

  /*
   * Receives an array of points and returns an object of each point in `points`, where each
   * point is an array of the `x`/`y` values of the points current location. The object
   * is key'd by the range id
   */
  _getPointsByRange (points) {
    return points.reduce((p, c) => {

      p[c.rangeId] = {
        x: this.previous.scalers.x.center(c.rangeId),
        y0: this.previous.yScale(c._range[0]),
        y1: this.previous.yScale(c._range[1]),
        raw: c.raw // So we can check if we actually have a raw value defined
      }

      return p
    }, {})
  }

  /*
   * Assumes the incoming `path` path has a stroke-dasharray and is currently
   * hidden. Draws the path up to the incoming `length`. All previous points are shown
   */
  _growPath (path, length, totalLength, previousPoints) {
    path.attr('stroke-dashoffset', totalLength - length)

    // Show previous points
    previousPoints.style('display', '')
  }

  /*
   * Opposite of `_growPath`. Shrinks the path hiding points behind the incoming
   * `length`
   */
  _shrinkPath (path, length, totalLength, previousPoints) {
    path.attr('stroke-dashoffset', -length)

    // Hide previous points
    previousPoints.style('display', 'none')
  }

  /*
   * Returns a x scaler for this chart. By default
   * it will use the argument `scalers` center scaler, unless this chart
   * doesn't have a breakdown, in which we always want to center the points
   * within the chart
   */
  _scalerX (scalers) {
    if (this.chart.layout.breakdown.calculated) {
      // Calculated breakdown means we don't have a breakdown
      return () => {
        return scalers.groupScale.range()[1] / 2
      }
    } else {
      return scalers.center
    }
  }

  /*
   * Update's the drawing points using scaler0 and scalers1
   * for the start and end scalers. If they aren't sent, we set
   * defaults based on the current state of this animation.
   */
  _points (group, scalers0, scalers1) {
    if (!scalers1) {
      scalers1 = {
        x: this._scalerX(this.scalers.x),
        y: this.yScale
      }
    }

    if (!scalers0) {
      scalers0 = scalers1
      if (this.previous.yScale) {
        scalers0 = {
          x: this._scalerX(this.previous.scalers.x),
          y: this.previous.yScale
        }
      }
    }

    group.call(points(scalers0, scalers1, this.size, this.chart, this.filled))
  }

  /*
   * Transitions the fromPoints to the style of toPoints. Also moves them to their correct location.
   */
  _transitionPoints (fromPoints, toPoints, opacity) {
    if (!fromPoints.size() && !toPoints.size()) {
      // Nothing to transition
      return
    }
    let circles = fromPoints.attr('transform', d => {
                              let toPoint = toPoints.data().find(to => to.rangeId === d.rangeId)
                              return `translate(${this.scalers.x.center(toPoint.rangeId)}, ${this.yScale(toPoint._range[1])})`
                            })
                            .style('opacity', opacity)
                            .selectAll('circle')

    circles.filter((d, i) => i === 0)
           .style('fill', d => {
             return toPoints.filter(to => to.rangeId == d.rangeId)
                            .selectAll('circle:nth-child(1)')
                            .style('fill')
           })
           .attr('r', toPoints.selectAll('circle:nth-child(1)').attr('r'))

    let innerCircle = toPoints.selectAll('circle:nth-child(2)')

    if (innerCircle.size() && !fromPoints.selectAll('circle:nth-child(2)').size()) {
      fromPoints.selection()
             .append('circle')
             .attr('class', 'inner')
             .attr('r', 0)
             .style('stroke-width', 0)
             .style('fill', () => {
              return innerCircle.style('fill')
           })
    }

    fromPoints.selectAll('circle') // Re-select so we get newly cricles
              .filter((d, i) => i > 0)
              .attr('r', innerCircle.size() ? innerCircle.attr('r') : 0)
  }

  /*
   * Creates a transition that walks the length of the `path` element within
   * the incoming `group` selection. Starts from the left and works right.
   * The incoming `transition` is used for inheriting from the parent transition.
   *
   * Pass a `pathAdjuster`, which will be invoked on each interval of the transition.
   */
  _walkLine (group, transition, pathAdjuster, initialOffset, subPathScalers) {
    let dataPoints = (group.datum().points || []).filter(point => {
      return point.raw !== undefined
    })

    if (!dataPoints.length) {
      // No points to walk
      return
    }

    let center = this._scalerX(this.scalers.x)
      , chart = this.chart
      , point = d3.select(group.node().querySelector('.point').cloneNode(true))
                  .classed('head', true) // Clone of the points displayed
                  .style('display', '')
      , t = group.transition(transition)
      , points = group.datum().points

    group.selectAll('path')
         .style('opacity', EPSILON) // Hide the main path for now

    this.pathAdjuster.insertSubPaths(group, initialOffset, subPathScalers)

    let subpaths = group.selectAll('.line.line-subpath')
      , durationPerTick = t.duration() / (this.scalers.x.groupScale.domain().length || 1) // prevent divide by zero

    // Add this leading point to the group
    group.node().appendChild(point.node())

    subpaths.transition()
            .duration(d => d.length * durationPerTick)
            .delay((d, i, all) => {
              let prev = [...all].slice(0, i)
                , prevTicks = array.sum(prev, node => {
                  return d3.select(node).datum().length
                })
              return prevTicks * durationPerTick
            })
            .tween('walk', function (d, i) {
              let totalLength = this.getTotalLength()
                , interpolateLength = interpolate(0, totalLength)

              return t => {
                let length = interpolateLength(t)
                  , coords = this.getPointAtLength(length)
                  , previousPoints = group.selectAll('.point:not(.head)')
                                          .filter(function (d, i) {
                                            return Math.round(center(d.rangeId)) <= Math.round(coords.x)
                                          })

                pathAdjuster(d3.select(this), length, totalLength, previousPoints)

                // Positions the leading point
                point.attr('transform', `translate(${coords.x}, ${coords.y})`)
              }
            })

    t.end().then(() => {
      // Once we're done transitioning, we can remove the leading point
      point.remove()

      // make sure all points are visible
      group.selectAll('.point').style('display', '')

      group.selectAll('.line-subpath').remove()

      // Cleanup the the stroke dasharray and offset which were used just for transitioning
      group.selectAll('path')
           .style('stroke-width', '') // Clear this property, so the css value is now used

      // Remove the opacity on the element so css has control
      group.selectAll('path')
           .style('opacity', '')
    }).catch(() => {
      // interrupted transition, ignore
    })

    return t
  }

}

export default LineAreaAnimation
